// src/components/SetupPrintLayout.js
import React from 'react';
import { Box, Typography, Grid, Paper, Divider } from '@mui/material';

// --- Funzioni Helper (invariate) ---
const PrintData = ({ label, value, unit = '', xs = 6, sm = 3 }) => (
  <Grid item xs={xs} sm={sm} sx={{ mb: 1 }}>
    <Typography variant="caption" display="block" color="textSecondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value || 'N/A'} {unit}</Typography>
  </Grid>
);

const PrintNote = ({ label, value, xs = 12 }) => (
  <Grid item xs={xs} sx={{ mb: 1 }}>
    <Typography variant="caption" display="block" color="textSecondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value || 'N/A'}</Typography>
  </Grid>
);

// --- MODIFICA: Rimosso React.forwardRef ---
const SetupPrintLayout = ({ setup }) => {
  if (!setup) {
    return null;
  }

  const { setupData: data = {}, eventName, sessionName, nomePilota, dataSessione } = setup;
  
  const displayDate = dataSessione 
    ? new Date(dataSessione.toDate()).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : 'N/A';

  // --- MODIFICA: Rimosso 'ref' dal Box ---
  return (
    <Box sx={{ padding: '16px', backgroundColor: '#fff', color: '#000' }}>
      {/* Intestazione */}
      <Box sx={{ borderBottom: '2px solid #ccc', mb: 2, pb: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1">{eventName} - {sessionName}</Typography>
        <Typography variant="body1">Date: {displayDate}</Typography>
      </Box>
      <Typography variant="h6" gutterBottom>Driver: {nomePilota || 'N/A'}</Typography>

      {/* Meteo */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Weather</Typography>
        <Grid container spacing={1}>
          <PrintData label="Air Temp" value={data.meteo?.tempAria} unit="°C" />
          <PrintData label="Track Temp" value={data.meteo?.tempAsfalto} unit="°C" />
          <PrintData label="Humidity" value={data.meteo?.umidita} unit="%" />
        </Grid>
      </Paper>

      {/* Pesi (Qui c'era il codice mancante) */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Weights & Balance</Typography>
        <Grid container spacing={1}>
            <PrintData label="Min. Reg. Weight" value={data.weights?.minRegWeight} unit="kg" />
            <PrintData label="Driver Weight" value={data.weights?.driverWeight} unit="kg" />
            <PrintData label="Dry Weight" value={data.weights?.dryWeight} unit="kg" />
            <PrintData label="Fuel Liters" value={data.weights?.fuelLiters} unit="L" />
            <PrintData label="Measured Total Weight" value={data.weights?.totalWeight} unit="kg" />
            <PrintData label="Applied Ballast" value={data.weights?.ballast} unit="kg" />
            <PrintData label="Front Weight (Axle)" value={data.weights?.totalFrontWeight} unit="kg" />
            <PrintData label="Rear Weight (Axle)" value={data.weights?.totalRearWeight} unit="kg" />
            <PrintData label="Weight Dist." value={data.weights?.weightRip} unit="%" />
            <PrintData label="FL" value={data.weights?.cornerFL} unit="kg" />
            <PrintData label="FR" value={data.weights?.cornerFR} unit="kg" />
            <PrintData label="RL" value={data.weights?.cornerRL} unit="kg" />
            <PrintData label="RR" value={data.weights?.cornerRR} unit="kg" />
        </Grid>
      </Paper>

      {/* Anteriore */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Front</Typography>
        <Grid container spacing={1}>
          <PrintData label="Toe FL" value={data.anteriore?.convergenzaSX} unit={`${data.anteriore?.toeInOrOutSX || 'out'} (mm)`} />
          <PrintData label="Toe FR" value={data.anteriore?.convergenzaDX} unit={`${data.anteriore?.toeInOrOutDX || 'out'} (mm)`} />
          <PrintData label="Camber FL" value={data.anteriore?.camberSX} unit="deg" />
          <PrintData label="Camber FR" value={data.anteriore?.camberDX} unit="deg" />
          <PrintData label="Caster FL" value={data.anteriore?.casterSX} unit="deg" />
          <PrintData label="Caster FR" value={data.anteriore?.casterDX} unit="deg" />
          <PrintData label="Target H. FL" value={data.anteriore?.altezzaTargetSX} unit="mm" />
          <PrintData label="Measured H. FL" value={data.anteriore?.altezzaMeasuredSX} unit="mm" />
          <PrintData label="Target H. FR" value={data.anteriore?.altezzaTargetDX} unit="mm" />
          <PrintData label="Measured H. FR" value={data.anteriore?.altezzaMeasuredDX} unit="mm" />
          <PrintData label="Spring FL" value={data.anteriore?.mollaSX} />
          <PrintData label="Spring FR" value={data.anteriore?.mollaDX} />
          <PrintData label="ARB Pos." value={data.anteriore?.barra} />
          <PrintData label="Bump FL" value={data.anteriore?.ammortizzatori?.bumpSX} />
          <PrintData label="Bump FR" value={data.anteriore?.ammortizzatori?.bumpDX} />
          <PrintData label="Rebound FL" value={data.anteriore?.ammortizzatori?.reboundSX} />
          <PrintData label="Rebound FR" value={data.anteriore?.ammortizzatori?.reboundDX} />
        </Grid>
      </Paper>

      {/* Posteriore */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
         <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Rear</Typography>
         <Grid container spacing={1}>
          <PrintData label="Toe RL" value={data.posteriore?.convergenzaSX} unit={`${data.posteriore?.toeInOrOutSX || 'in'} (mm)`} />
          <PrintData label="Toe RR" value={data.posteriore?.convergenzaDX} unit={`${data.posteriore?.toeInOrOutDX || 'in'} (mm)`} />
          <PrintData label="Camber RL" value={data.posteriore?.camberSX} unit="deg" />
          <PrintData label="Camber RR" value={data.posteriore?.camberDX} unit="deg" />
          <PrintData label="Target H. RL" value={data.posteriore?.altezzaTargetSX} unit="mm" />
          <PrintData label="Measured H. RL" value={data.posteriore?.altezzaMeasuredSX} unit="mm" />
          <PrintData label="Target H. RR" value={data.posteriore?.altezzaTargetDX} unit="mm" />
          <PrintData label="Measured H. RR" value={data.posteriore?.altezzaMeasuredDX} unit="mm" />
          <PrintData label="Spring RL" value={data.posteriore?.mollaSX} />
          <PrintData label="Spring RR" value={data.posteriore?.mollaDX} />
          <PrintData label="ARB Pos." value={data.posteriore?.barra} />
          <PrintData label="Bump RL" value={data.posteriore?.ammortizzatori?.bumpSX} />
          <PrintData label="Bump RR" value={data.posteriore?.ammortizzatori?.bumpDX} />
          <PrintData label="Rebound RL" value={data.posteriore?.ammortizzatori?.reboundSX} />
          <PrintData label="Rebound RR" value={data.posteriore?.ammortizzatori?.reboundDX} />
         </Grid>
      </Paper>

      {/* Gomme */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Tires</Typography>
        <Typography variant="subtitle1" gutterBottom>Front Left</Typography>
        <Grid container spacing={1}>
            <PrintData label="P. Cold" value={data.gomme_ant_sx?.pFreddo} unit="bar" xs={6} sm={2} />
            <PrintData label="P. Hot" value={data.gomme_ant_sx?.pCaldo} unit="bar" xs={6} sm={2} />
            <PrintData label="T. Int" value={data.gomme_ant_sx?.tInt} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Mid" value={data.gomme_ant_sx?.tMed} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Ext" value={data.gomme_ant_sx?.tExt} unit="°C" xs={4} sm={2} />
        </Grid>
        <Typography variant="subtitle1" gutterBottom>Front Right</Typography>
        <Grid container spacing={1}>
            <PrintData label="P. Cold" value={data.gomme_ant_dx?.pFreddo} unit="bar" xs={6} sm={2} />
            <PrintData label="P. Hot" value={data.gomme_ant_dx?.pCaldo} unit="bar" xs={6} sm={2} />
            <PrintData label="T. Int" value={data.gomme_ant_dx?.tInt} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Mid" value={data.gomme_ant_dx?.tMed} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Ext" value={data.gomme_ant_dx?.tExt} unit="°C" xs={4} sm={2} />
        </Grid>
        <Typography variant="subtitle1" gutterBottom>Rear Left</Typography>
        <Grid container spacing={1}>
            <PrintData label="P. Cold" value={data.gomme_post_sx?.pFreddo} unit="bar" xs={6} sm={2} />
            <PrintData label="P. Hot" value={data.gomme_post_sx?.pCaldo} unit="bar" xs={6} sm={2} />
            <PrintData label="T. Int" value={data.gomme_post_sx?.tInt} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Mid" value={data.gomme_post_sx?.tMed} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Ext" value={data.gomme_post_sx?.tExt} unit="°C" xs={4} sm={2} />
        </Grid>
        <Typography variant="subtitle1" gutterBottom>Rear Right</Typography>
        <Grid container spacing={1}>
            <PrintData label="P. Cold" value={data.gomme_post_dx?.pFreddo} unit="bar" xs={6} sm={2} />
            <PrintData label="P. Hot" value={data.gomme_post_dx?.pCaldo} unit="bar" xs={6} sm={2} />
            <PrintData label="T. Int" value={data.gomme_post_dx?.tInt} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Mid" value={data.gomme_post_dx?.tMed} unit="°C" xs={4} sm={2} />
            <PrintData label="T. Ext" value={data.gomme_post_dx?.tExt} unit="°C" xs={4} sm={2} />
        </Grid>
      </Paper>

      {/* Aero, Diff. & Carburante */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Aero, Diff. & Fuel</Typography>
        <Grid container spacing={1}>
            <PrintData label="Rear Wing Setting" value={data.aero?.alaSetting} />
            <PrintData label="Rear Wing Angle" value={data.aero?.alaAngle} unit="deg" />
            <PrintData label="Splitter" value={data.aero?.splitter} />
            <PrintData label="Fuel Consumption" value={data.differenziale?.fuelConsumption} unit="L/lap" />
            <PrintData label="Diff. Pre-Load" value={data.differenziale?.precarico} unit="Nm" />
            <PrintData label="Diff. Lock" value={data.differenziale?.bloccaggio} unit="%" />
        </Grid>
        <Divider sx={{ my: 1 }} />
        <Grid container spacing={1}>
          <PrintNote label="Gear Ratios" value={data.differenziale?.gearRatios} xs={12} />
        </Grid>
      </Paper>

      {/* Feedback & Tempi */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Feedback & Times</Typography>
        <Grid container spacing={1}>
          <PrintData label="Best Lap" value={data.feedback?.bestLap} xs={12} sm={6} />
          <PrintNote label="Times List" value={data.feedback?.listaTempi} xs={12} />
          <PrintNote label="Technician Notes" value={data.feedback?.noteTecnico} xs={12} />
          <PrintNote label="Driver Feedback" value={data.feedback?.feedbackPilota} xs={12} />
          <PrintNote label="Telemetry Link" value={data.feedback?.linkTelemetria} xs={12} sm={6} />
          <PrintNote label="Telemetry Desc." value={data.feedback?.descrizioneTelemetria} xs={12} sm={6} />
          <PrintNote label="Video Link" value={data.feedback?.linkVideo} xs={12} sm={6} />
          <PrintNote label="Video Desc." value={data.feedback?.descrizioneVideo} xs={12} sm={6} />
        </Grid>
      </Paper>

    </Box>
  );
};

export default SetupPrintLayout;